import React, { ReactNode } from "react";
import {
  Routes,
  Route,
  Navigate,
  NavigateFunction,
  Params,
} from "react-router-dom";
import { from, Subject, takeUntil } from "rxjs";
import { Provider, inject, observer } from "mobx-react";
import ServiceStore from "./ServiceStore";
import { UpsertServiceActiveStep } from "./Service.enum";
import ServiceProtectedRoutes from "./ServiceProtectedRoutes";
import PricingPage from "./components/PricingPage";
import OptionsPage from "./components/OptionsPage";
import {
  AuthService,
  FlexContainer,
  Loader,
  RedButton,
  SapphireBlueButton,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { CategoryService, ServiceAPIService } from "../../shared/services";
import {
  Header,
  ItemDetailPage,
  ItemHeader,
  Layout,
  SideBar,
} from "../../shared/components";
import { RolesAndPermissionStore } from "../../shared/store";
import { ProductDetailWrapper } from "../../shared/styles";
import {
  CountryWithStateModel,
  ItemDetailInputModel,
  MediaModel,
} from "../../shared/models";
import { toJS } from "mobx";
import { MediaTypes } from "../../shared/enums";
import FullLibraryView from "../../shared/components/FullLibraryView";
import { getMerchantAccountStore } from "../merchantAccounts/MerchantAccountStore";

interface IProps {
  serviceStore?: ServiceStore;
  navigate: NavigateFunction;
  params: Readonly<Params<string>>;
  rolesAndPermissionStore: RolesAndPermissionStore;
  countryDataLoaded: false;
  countriesWithState: CountryWithStateModel[];
}

type IState = {
  showLibraryView: boolean;
  showUpload?: boolean;
};

export const getCategoryService = (() => {
  let instance: CategoryService | null = null;
  return () => {
    if (!instance) {
      instance = new CategoryService();
    }
    return instance;
  };
})();

@inject("serviceStore")
@observer
class ServiceRoutes extends React.Component<IProps, IState> {
  private loader: Loader = new Loader(false);
  private $destroy: Subject<boolean> = new Subject<boolean>();
  private disposeSurchargeDefault?: () => void;

  constructor(props: IProps) {
    super(props);
    this.state = {
      showLibraryView: false,
      showUpload: false,
    };
  }

  toggleLibraryView = (show: boolean, fromUpload: boolean = false) => {
    this.setState(() => ({
      showLibraryView: show,
      showUpload: fromUpload,
    }));
  };

  componentDidMount(): void {
    this.props.serviceStore?.reset();

    if (this.serviceId) {
      this.loader.show();
      ServiceAPIService.getService(
        AuthService.authData.accountId,
        this.serviceId,
      )
        .pipe(takeUntil(this.$destroy))
        .subscribe({
          next: (result) => {
            if (!result.result) {
              ToastService.showError("Error in loading service details");
            } else {
              this.loader.hide();
              this.props.serviceStore?.constructStoreFromAPI(result.result);
            }
          },
          error: () => {
            ToastService.showError("Error in loading service details");
          },
        });
    } else {
      this.setDefaultSurcharge();
    }
  }

  /* A new service picks up the surcharge setting of the merchant account it
     will be sold through. Only runs when creating: an existing service keeps
     the setting it was saved with. */
  setDefaultSurcharge = () => {
    this.disposeSurchargeDefault =
      getMerchantAccountStore().whenItemSurchargeDefaultKnown((surcharge) => {
        const { serviceStore } = this.props;
        if (!serviceStore) return;
        const updated = ItemDetailInputModel.withMerchantSurchargeDefault(
          serviceStore.detailPage,
          surcharge,
        );
        if (updated) serviceStore.updateDetailsPage(updated);
      });
  };

  onCancel() {
    if (
      this.props.serviceStore?.activeStep === UpsertServiceActiveStep.Pricing
    ) {
      this.onBackOrNext(false);
      return;
    }
    this.props.navigate(AuthService.prefixRoute + "items");
  }

  get serviceId() {
    const split = ((this.props.params && this.props.params["*"]) || "").split(
      "/",
    );
    const id = split[split.length - 1];
    if (UtilityMethods.isValidObjectId(id)) {
      return id;
    }
    return null;
  }

  componentWillUnmount(): void {
    this.$destroy.next(true);
    this.disposeSurchargeDefault?.();
  }

  onBackOrNext(isNext: boolean) {
    if (this.loader.showLoader) return;
    let queryParam: string = "";
    if (this.serviceId) {
      queryParam = "/" + this.serviceId;
    }
    if (isNext) {
      const success = this.props.serviceStore?.validate();
      if (!success) return;
    }
    switch (this.props.serviceStore?.activeStep) {
      case UpsertServiceActiveStep.Detail:
        this.props.serviceStore?.updateActiveStep(
          UpsertServiceActiveStep.Pricing,
        );
        this.props.navigate(`/app/service/pricing${queryParam}`);
        break;
      case UpsertServiceActiveStep.Pricing:
        isNext
          ? this.props.serviceStore?.updateActiveStep(
              UpsertServiceActiveStep.Options,
            )
          : this.props.serviceStore?.updateActiveStep(
              UpsertServiceActiveStep.Detail,
            );
        isNext
          ? this.props.navigate(`/app/service/options${queryParam}`)
          : this.props.navigate(`/app/service/detail${queryParam}`);
        break;

      case UpsertServiceActiveStep.Options:
        this.props.serviceStore?.updateActiveStep(
          UpsertServiceActiveStep.Pricing,
        );
        this.props.navigate(`/app/service/pricing${queryParam}`);

        break;
    }
  }

  stepConfig = {
    [UpsertServiceActiveStep.Detail]: {
      name: "detail",
      url: "/app/service/detail",
    },
    [UpsertServiceActiveStep.Pricing]: {
      name: "pricing",
      url: "/app/service/pricing",
    },
    [UpsertServiceActiveStep.Options]: {
      name: "options",
      url: "/app/service/options",
    },
  };

  onTabChange = (key: string) => {
    const step = key as UpsertServiceActiveStep;
    const queryParam: string = this.serviceId || "";

    const validationResult =
      this.props.serviceStore?.validatePreviousSteps(step);
    if (!validationResult) {
      ToastService.showError("Unable to validate steps.");
      return;
    }

    const { isValid, errorStep } = validationResult;

    if (!isValid && errorStep) {
      ToastService.showError(
        `Please fill all the required fields in service ${this.stepConfig[errorStep].name} before proceeding.`,
      );
      this.props.serviceStore?.updateActiveStep(errorStep);
      this.props.navigate(`${this.stepConfig[errorStep].url}/${queryParam}`);
      return;
    }

    this.props.serviceStore?.updateActiveStep(step);
    this.props.navigate(`${this.stepConfig[step].url}/${queryParam}`);
  };

  onSave = (isSaveAndExit: boolean = false) => {
    if (this.loader.showLoader) return;

    if (isSaveAndExit) {
      const validationResult = this.props.serviceStore?.validatePreviousSteps(
        UpsertServiceActiveStep.Options,
      );
      if (!validationResult) {
        ToastService.showError("Validation failed.");
        return;
      }
      const { isValid, errorStep } = validationResult;
      if (!isValid && errorStep) {
        ToastService.showError(
          `Please fill all the required fields in service ${this.stepConfig[errorStep].name} before saving.`,
        );
        this.props.serviceStore?.updateActiveStep(errorStep);
        this.props.navigate(
          `${this.stepConfig[errorStep].url}/${this.serviceId || ""}`,
        );
        return;
      }
      const isOptionsValid = this.props.serviceStore?.validate(
        UpsertServiceActiveStep.Options,
      );
      if (!isOptionsValid) {
        ToastService.showError(
          "Please fill all required fields in the Options step before saving.",
        );
        this.props.serviceStore?.updateActiveStep(
          UpsertServiceActiveStep.Options,
        );
        this.props.navigate(
          `${this.stepConfig[UpsertServiceActiveStep.Options].url}/${
            this.serviceId || ""
          }`,
        );
        return;
      }
    } else {
      const success = this.props.serviceStore?.validate();
      if (!success) return;
    }

    if (this.props.serviceStore?.isSaveUpdateInProgress) return;
    this.props.serviceStore?.toggleSaveUpdateInProgress();

    if (!this.props.serviceStore) return;
    const request = this.props.serviceStore?.createAPIModel();

    if (this.serviceId) {
      request.id = this.serviceId;
      ServiceAPIService.update(request).subscribe({
        next: (response) => {
          if (response) {
            ToastService.showSuccess("Service updated Successfully");
            this.props.serviceStore?.toggleSaveUpdateInProgress();
            if (isSaveAndExit) {
              this.props.navigate("/app/items");
            }
          }
        },
        error: () => {
          ToastService.showError("Service update failed!");
          this.props.serviceStore?.toggleSaveUpdateInProgress();
        },
      });
    } else {
      ServiceAPIService.save(request).subscribe({
        next: (response) => {
          if (response.id) {
            ToastService.showSuccess("Saved Successfully");
            this.props.serviceStore?.toggleSaveUpdateInProgress();
            if (isSaveAndExit) {
              this.props.navigate("/app/items");
            }
          }
        },
        error: () => {
          ToastService.showError("Error in saving");
          this.props.serviceStore?.toggleSaveUpdateInProgress();
        },
      });
    }
  };

  get showNextButton(): boolean {
    return (
      this.props.serviceStore?.activeStep !== UpsertServiceActiveStep.Options
    );
  }

  get showBackbutton(): boolean {
    return (
      this.props.serviceStore?.activeStep !== UpsertServiceActiveStep.Detail
    );
  }

  get headerCancelButtonCaption(): string {
    return this.props.serviceStore?.activeStep ===
      UpsertServiceActiveStep.Pricing
      ? "Back"
      : "";
  }

  onSelectLibraryMedia(media: MediaModel) {
    this.props.serviceStore?.updateDetailsPage(
      new ItemDetailInputModel({
        ...this.props.serviceStore?.detailPage,
        medias: [...this.props.serviceStore?.detailPage.medias, media],
        showLibrary: false,
      }),
    );
  }

  deleteMedia(medias: MediaModel[]) {
    this.props.serviceStore?.updateDetailsPage(
      new ItemDetailInputModel({
        ...this.props.serviceStore?.detailPage,
        medias,
      }),
    );
  }

  onFavourite(media: MediaModel) {
    const updatedMedia = (
      toJS(this.props.serviceStore?.detailPage?.medias) || []
    ).map((x) => {
      if (x.mediaId === media.mediaId) {
        return { ...x, isFavourite: true } as MediaModel;
      } else if (x.isFavourite) {
        return { ...x, isFavourite: false } as MediaModel;
      }
      return x;
    });
    const index = updatedMedia.findIndex((x) => x.mediaId === media.mediaId);
    if (index !== -1) {
      const [favouritedMedia] = updatedMedia.splice(index, 1);
      updatedMedia.unshift(favouritedMedia);
    }
    this.props.serviceStore?.updateDetailsPage(
      new ItemDetailInputModel({
        ...this.props.serviceStore?.detailPage,
        medias: [...updatedMedia],
      }),
    );
  }

  onDelete(media: MediaModel) {
    const updatedMedia = (
      toJS(this.props.serviceStore?.detailPage?.medias) || []
    ).map((x) => x);
    const index = updatedMedia.findIndex((x) => x.mediaId === media.mediaId);
    updatedMedia.splice(index, 1);
    this.deleteMedia(updatedMedia);
  }

  updateMedia(medias: MediaModel[]) {
    this.props.serviceStore?.updateDetailsPage(
      new ItemDetailInputModel({
        ...this.props.serviceStore?.detailPage,
        medias: [...this.props.serviceStore?.detailPage.medias, ...medias],
      }),
    );
  }

  get footerButtons() {
    return (
      <FlexContainer
        alignItems="center"
        justifyContent="center"
        spacing={15}
        size={0}
        padding="10px 0px 0px"
      >
        <RedButton height="40px" onClick={() => this.onCancel()}>
          Cancel
        </RedButton>
        {this.showBackbutton && (
          <SapphireBlueButton
            height="40px"
            onClick={() => this.onBackOrNext(false)}
          >
            Back
          </SapphireBlueButton>
        )}

        {this.showNextButton && (
          <SapphireBlueButton
            height="40px"
            onClick={() => this.onBackOrNext(true)}
          >
            Next
          </SapphireBlueButton>
        )}
      </FlexContainer>
    );
  }
  get routes() {
    return (
      <Routes>
        <Route
          path={`/detail/:id?`}
          element={
            <ServiceProtectedRoutes currentRoute="detail">
              <ProductDetailWrapper>
                <ItemHeader header="Service Details" />
                <ItemDetailPage
                  isProduct={false}
                  itemDetail={this.props.serviceStore?.detailPage}
                  onUpdate={(detail: ItemDetailInputModel) =>
                    this.props.serviceStore?.updateDetailsPage(detail)
                  }
                  toggleLibrary={(show: boolean, fromUpload: boolean) =>
                    this.toggleLibraryView(show, fromUpload)
                  }
                  showLibraryView={this.state.showLibraryView}
                />
              </ProductDetailWrapper>
            </ServiceProtectedRoutes>
          }
        />
        <Route
          path={`/pricing/:id?`}
          element={
            <ServiceProtectedRoutes currentRoute="pricing">
              <>
                <PricingPage />
              </>
            </ServiceProtectedRoutes>
          }
        />

        <Route
          path={`/options/:id?`}
          element={
            <ServiceProtectedRoutes currentRoute="options">
              <>
                <OptionsPage
                  serviceId={this.serviceId}
                  countriesWithState={this.props.countriesWithState || []}
                />
              </>
            </ServiceProtectedRoutes>
          }
        />
        <Route path="*" element={<Navigate to="/app/service/detail" />} />
      </Routes>
    );
  }

  render(): ReactNode {
    if (!this.props.countryDataLoaded) {
      return <>{new Loader().loaderComponent}</>;
    }
    if (this.state.showLibraryView) {
      return (
        <FullLibraryView
          multiple={true}
          medias={toJS(this.props.serviceStore?.detailPage.medias || [])}
          onUpload={(medias: MediaModel[]) => this.updateMedia(medias)}
          onDelete={(media: MediaModel) => this.onDelete(media)}
          onSelectLibraryMedia={(media) =>
            this.onSelectLibraryMedia(MediaModel.mediaItemToMediaModel(media))
          }
          onFavourite={(media: MediaModel) => this.onFavourite(media)}
          allowedMediaTypes={[MediaTypes.Image, MediaTypes.Video]}
          toggleLibrary={(show) => this.toggleLibraryView(show)}
          showLibrary={this.state.showLibraryView}
          updateMedia={(medias: MediaModel[]) => this.updateMedia(medias)}
          showUpload={this.state.showUpload}
        />
      );
    }
    return (
      <Provider categoryService={getCategoryService()}>
        <Layout
          sideBarComponent={
            <SideBar
              activeStep={this.props.serviceStore?.activeStep?.toString() || ""}
              showVariant={false}
              onTabChange={(key: UpsertServiceActiveStep) =>
                this.onTabChange(key)
              }
            />
          }
          headerCaption={""}
          saveAndExitClick={() => this.onSave(true)}
          showSaveAndExit={this.serviceId ? true : false}
          disableSaveAndExit={
            this.loader.showLoader ||
            !!this.props.serviceStore?.isSaveUpdateInProgress
          }
          cancelClick={() => this.onCancel()}
          nextButtonCaption={this.showNextButton ? "Next" : ""}
          nextClick={() => this.onBackOrNext(true)}
          showBackButton={this.showBackbutton}
          showNextButton={this.showNextButton}
          backClick={() => this.onBackOrNext(false)}
          showCancelButton={false}
          headerComponent={
            <Header
              headerCaption={"CREATE YOUR ITEM"}
              showSaveButton={!this.showNextButton}
              disableSaveButton={
                this.props.serviceStore?.isSaveUpdateInProgress ||
                !this.serviceId
              }
              showSaveAndExitButton={true}
              disableSaveAndExitButton={
                this.loader.showLoader ||
                !!this.props.serviceStore?.isSaveUpdateInProgress
              }
              saveAndExitButtonCaption={
                this.showNextButton ? "NEXT" : "SAVE & EXIT"
              }
              saveClick={() => this.onSave()}
              cancelClick={() => this.onCancel()}
              cancelButtonCaption={this.headerCancelButtonCaption}
              saveAndExitClick={() => {
                if (this.loader.showLoader) return;
                this.showNextButton
                  ? this.onBackOrNext(this.showNextButton)
                  : this.onSave(true);
              }}
            />
          }
          showHeader={false}
          showFooter={!this.loader.showLoader}
          // noScroll={true}
        >
          {this.loader.showLoader ? this.loader.loaderComponent : this.routes}
        </Layout>
      </Provider>
    );
  }
}

export default withRouter(ServiceRoutes);
